import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Contractor } from "@/types/contractor";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ContractorCard } from "@/components/ContractorCard";
import { LoadingState } from "@/components/LoadingState";

const OklahomaCity = () => {
  console.log('Rendering Oklahoma City page');

  const { data: contractors = [], isLoading } = useQuery({
    queryKey: ['contractors', 'oklahoma-city'],
    queryFn: async () => {
      console.log('Fetching contractors for Oklahoma City page');
      const { data, error } = await supabase
        .from('contractors')
        .select('*')
        .eq('location', 'oklahoma-city')
        .order('featured', { ascending: false })
        .order('rating', { ascending: false });

      if (error) {
        console.error('Error fetching contractors:', error);
        throw error;
      }

      console.log('Successfully fetched contractors:', data);
      return data as Contractor[];
    },
  });

  const breadcrumbItems = [
    {
      label: "Oklahoma City",
    },
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Best Contractors in Oklahoma City | Home Pros OKC",
    "description": "Find trusted contractors in Oklahoma City for roofing, plumbing, window cleaning, and more. Let Home Pros OKC connect you with top professionals.",
    "url": "https://homeprosokc.com/top-contractors-in-oklahoma-city",
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": contractors.map((contractor, index) => ({
        "@type": "LocalBusiness",
        "position": index + 1,
        "name": contractor.name,
        "telephone": contractor.phone,
        "url": contractor.website,
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Oklahoma City",
          "addressRegion": "OK",
          "addressCountry": "US"
        }
      }))
    }
  };

  const serviceCategories = [
    {
      title: "Window Cleaning",
      description: "Professional window cleaning services to keep your home sparkling clean and improve curb appeal.",
      link: "/services/window-cleaning",
    },
    {
      title: "Roofing",
      description: "Expert roofing contractors for installations, repairs, and maintenance to protect your home.",
      link: "/services/roofing",
    },
    {
      title: "Plumbing",
      description: "Licensed plumbers for all your residential plumbing needs, from repairs to installations.",
      link: "/services/plumbing",
    },
    {
      title: "Electrical",
      description: "Certified electricians providing safe and reliable electrical services for your home.",
      link: "/services/electrical",
    },
  ];

  if (isLoading) {
    return <LoadingState />;
  }

  return (
    <div className="min-h-screen">
      <SEO
        title="Best Contractors in Oklahoma City | Home Pros OKC"
        description="Find trusted contractors in Oklahoma City for roofing, plumbing, window cleaning, and more. Let Home Pros OKC connect you with top professionals."
        canonicalUrl="/top-contractors-in-oklahoma-city"
        schema={schema}
      />

      <div className="container px-4 py-8 mx-auto max-w-7xl">
        <Breadcrumbs items={breadcrumbItems} />
        
        {/* Hero Section */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <h1 className="text-4xl font-bold mb-6">Best Contractors in Oklahoma City</h1>
          <p className="text-lg text-muted-foreground">
            Home Pros OKC connects you with trusted, experienced contractors in Oklahoma City. 
            From window cleaning and roofing to plumbing and electrical work, our network of 
            verified professionals ensures quality service for all your home improvement needs.
          </p>
        </div>

        {/* Services Section */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold mb-8">Top Services in Oklahoma City</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {serviceCategories.map((service) => (
              <Link 
                key={service.title}
                to={service.link}
                className="block p-6 rounded-lg border bg-card hover:shadow-lg transition-shadow"
              >
                <h3 className="text-xl font-semibold mb-2">{service.title}</h3>
                <p className="text-muted-foreground mb-4">{service.description}</p>
                <span className="text-primary hover:text-primary/80">
                  Find {service.title} Professionals →
                </span>
              </Link>
            ))}
          </div>
        </section>

        {/* Featured Contractors Section */}
        {contractors.filter(c => c.featured).length > 0 && (
          <section className="mb-16">
            <h2 className="text-2xl font-semibold mb-8">Featured Contractors in OKC</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {contractors
                .filter(c => c.featured)
                .map((contractor) => (
                  <ContractorCard
                    key={contractor.id}
                    contractor={contractor}
                    viewMode="grid"
                  />
                ))}
            </div>
          </section>
        )}

        {/* Regular Contractors Section */}
        {contractors.filter(c => !c.featured).length > 0 && (
          <section className="mb-16">
            <h2 className="text-2xl font-semibold mb-8">More Top-Rated Contractors</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {contractors
                .filter(c => !c.featured)
                .map((contractor) => (
                  <ContractorCard
                    key={contractor.id}
                    contractor={contractor}
                    viewMode="grid"
                  />
                ))}
            </div>
          </section>
        )}

        {/* CTA Section */}
        <section className="text-center bg-muted/50 rounded-lg p-8 mb-16">
          <h2 className="text-2xl font-semibold mb-4">
            Looking for the Best Contractor for Your Project?
          </h2>
          <p className="text-lg text-muted-foreground mb-6">
            Browse our trusted professionals in Oklahoma City and find the perfect match for your home improvement needs.
          </p>
          <Link to="/">
            <Button size="lg" className="bg-primary hover:bg-primary/90">
              Explore All Contractors
            </Button>
          </Link>
        </section>
      </div>
    </div>
  );
};

export default OklahomaCity;